<template>
  <div id="app-shell" class="min-h-screen bg-surface text-on-surface">
    <!-- ====== Stack overlay (full-screen, slides over tab content) ====== -->
    <transition :name="stackTransition">
      <div
        v-if="isStackRoute"
        id="app-stack"
        class="fixed inset-0 z-60 bg-surface overflow-y-auto overflow-x-hidden"
        style="-webkit-overflow-scrolling: touch; overscroll-behavior: contain;"
      >
        <router-view />
      </div>
    </transition>

    <!-- ====== Main tab area（v-show 保持各 Tab 挂载，滚动位置不丢失） ====== -->
    <div :class="{ 'opacity-0 pointer-events-none': isStackRoute }" class="transition-opacity duration-100">
      <keep-alive :include="tabPageNames">
        <component :is="activeTabComponent" :key="activeTab" v-show="!isStackRoute" />
      </keep-alive>
    </div>

    <!-- ====== Tab bar ====== -->
    <AppTabbar
      v-if="showTabbar"
      :active-tab="activeTab"
      @switch="onTabSwitch"
    />

    <div
      class="fixed bottom-24 left-1/2 bg-on-surface text-surface px-6 py-3 rounded-full shadow-2xl z-[100] pointer-events-none"
      :class="toastVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'"
      style="transform: translateX(-50%); transition: opacity 0.3s, transform 0.3s;"
    >
      <div class="flex items-center gap-2">
        <span class="material-symbols-outlined text-[20px]">info</span>
        <span class="font-label-md text-label-md">{{ toastMessage }}</span>
      </div>
    </div>

    <div
      v-if="showPromoPopup && !isStackRoute"
      class="promo-overlay"
      @click.self="closePromoPopup"
    >
      <div class="promo-dialog">
        <button
          class="promo-close"
          type="button"
          aria-label="关闭"
          @click="closePromoPopup"
        >
          <span class="material-symbols-outlined">close</span>
        </button>
        <button v-if="promoImg" class="promo-image-btn" type="button" @click="goToActivityRules">
          <img class="promo-image" :src="promoImg" alt="Panda Campus 开学季福利，点击查看活动细则" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AppTabbar from '@/components/AppTabbar.vue';
import HomePage from '@/views/HomePage.vue';
import NearbyPage from '@/views/NearbyPage.vue';
import PublishPage from '@/views/PublishPage.vue';
import WantedListPage from '@/views/WantedListPage.vue';
import ProfilePage from '@/views/ProfilePage.vue';
import store from '@/store';
import getApi from '@/api';

const api = getApi();
const API_IMAGE_BASE = 'https://api.pandacampus.com';

const TAB_NAMES = ['home', 'nearby', 'publish', 'wanted-list', 'profile'];
const TAB_COMPONENT_NAMES = ['HomePage', 'NearbyPage', 'PublishPage', 'wanted-list', 'profile'];
const TAB_COMPONENT_MAP = {
  home: 'HomePage',
  nearby: 'NearbyPage',
  publish: 'PublishPage',
  'wanted-list': 'WantedListPage',
  profile: 'ProfilePage'
};
const PENDING_TOAST_KEY = 'pendingToast';

export default {
  name: 'AppShell',
  components: {
    AppTabbar,
    HomePage,
    NearbyPage,
    PublishPage,
    WantedListPage,
    ProfilePage
  },
  data() {
    return {
      stackTransition: 'stack-slide-left',
      lastTab: 'home',
      toastVisible: false,
      toastMessage: '',
      toastTimer: null,
      promoImg: '',
      showPromoPopup: false
    };
  },
  computed: {
    isStackRoute() {
      return !!this.$route.meta.stack;
    },
    activeTab() {
      return this.$route.meta.tab || this.lastTab;
    },
    activeTabComponent() {
      return TAB_COMPONENT_MAP[this.activeTab] || 'HomePage';
    },
    showTabbar() {
      return !this.isStackRoute && !this.$route.meta.hideTabbar;
    },
    tabPageNames() {
      return TAB_COMPONENT_NAMES.join(',');
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(to, from) {
        this.consumePendingToast();
        if (to.meta.tab) {
          this.lastTab = to.meta.tab;
        }
        if (to.name === 'home') {
          this.maybeShowPromoPopup();
        } else {
          this.showPromoPopup = false;
        }

        // Determine slide direction for stack transitions
        if (from && to.meta.stack && from.meta.stack) {
          this.stackTransition = 'stack-slide-left';
        } else if (from && to.meta.stack && !from.meta.stack) {
          this.stackTransition = 'stack-slide-left';
        } else if (from && !to.meta.stack && from.meta.stack) {
          this.stackTransition = 'stack-slide-right';
        }

        // Emit page lifecycle events for backward compatibility
        this.$nextTick(() => {
          if (to.meta.tab) {
            this.$root.$emit('page:activate', { page: to.meta.tab, type: 'tab' });
            document.dispatchEvent(new CustomEvent('page:activate', { detail: { page: to.meta.tab, type: 'tab' } }));
          }
          if (from && from.meta && from.meta.tab) {
            this.$root.$emit('page:deactivate', { page: from.meta.tab, type: 'tab' });
            document.dispatchEvent(new CustomEvent('page:deactivate', { detail: { page: from.meta.tab, type: 'tab' } }));
          }
        });
      }
    }
  },
  methods: {
    showToast(message) {
      this.toastMessage = message || '';
      this.toastVisible = true;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false;
      }, 2500);
    },

    consumePendingToast() {
      const message = sessionStorage.getItem(PENDING_TOAST_KEY);
      if (!message) return;
      sessionStorage.removeItem(PENDING_TOAST_KEY);
      this.showToast(message);
    },

    hasPublished() {
      return Number(store.state.userinfo && store.state.userinfo.switch) === 1;
    },
    normalizeImageUrl(src) {
      if (!src) return '';
      var url = String(src).trim();
      if (!url) return '';
      if (url.indexOf('http://') === 0) {
        url = 'https://' + url.slice(7);
      }
      if (/^https?:\/\//i.test(url)) return url;
      if (url.charAt(0) === '/') return API_IMAGE_BASE + url;
      return url;
    },
    closePromoPopup() {
      this.showPromoPopup = false;
    },
    goToActivityRules() {
      this.showPromoPopup = false;
      this.$router.push({ name: 'activity-rules' });
    },
    loadPromoImage() {
      if (this.promoImg) {
        return Promise.resolve(this.promoImg);
      }
      return api.getHomeActivity()
        .then((src) => {
          this.promoImg = this.normalizeImageUrl(src);
          return this.promoImg;
        })
        .catch(() => {
          this.promoImg = '';
          return '';
        });
    },
    maybeShowPromoPopup() {
      if (this.hasPublished()) {
        this.showPromoPopup = false;
        return;
      }

      var switchReady = Promise.resolve();
      if (store.isLoggedIn) {
        switchReady = api.userInfo(store.state.token)
          .then((profile) => {
            store.setUserinfo(profile || {});
          })
          .catch(() => {});
      }

      Promise.all([this.loadPromoImage(), switchReady]).then(() => {
        if (this.$route.name !== 'home') return;
        if (this.hasPublished() || !this.promoImg) {
          this.showPromoPopup = false;
          return;
        }
        this.showPromoPopup = true;
      });
    },
    onTabSwitch(tab) {
      if (tab === this.activeTab) return;
      if (tab === 'publish' && !store.isLoggedIn) {
        this.showToast('未登录，请先登录');
        return;
      }
      if (TAB_NAMES.indexOf(tab) !== -1) {
        this.$router.push({ name: tab });
      }
    }
  },
  mounted() {
    this.lastTab = this.$route.meta.tab || 'home';
    this.consumePendingToast();
  },
  beforeDestroy() {
    clearTimeout(this.toastTimer);
  }
};
</script>

<style>
/* Stack slide transitions */
.stack-slide-left-enter-active,
.stack-slide-left-leave-active,
.stack-slide-right-enter-active,
.stack-slide-right-leave-active {
  transition: transform 0.25s ease-out;
}

.stack-slide-left-enter {
  transform: translateX(100%);
}
.stack-slide-left-leave-to {
  transform: translateX(-30%);
}

.stack-slide-right-enter {
  transform: translateX(-30%);
}
.stack-slide-right-leave-to {
  transform: translateX(100%);
}

/* Tab switching */
#app-shell .transition-opacity {
  transition: opacity 0.1s ease-out;
}

/* 各 Tab 页独立滚动容器 */
.tab-page-root {
  height: 100dvh;
  overflow: hidden;
}
.tab-page-scroll {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}
.promo-overlay {
  position: fixed;
  inset: 0;
  z-index: 80;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 12px 96px;
  box-sizing: border-box;
}
.promo-dialog {
  position: relative;
  width: min(94vw, 640px);
  max-height: calc(100dvh - 152px);
}
.promo-close {
  position: absolute;
  top: -44px;
  right: 0;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.92);
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.promo-close .material-symbols-outlined {
  font-size: 22px;
}
.promo-image-btn {
  display: block;
  width: 100%;
  max-height: calc(100dvh - 152px);
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
  cursor: pointer;
}
.promo-image {
  display: block;
  width: 100%;
  height: auto;
  max-height: calc(100dvh - 152px);
  object-fit: contain;
}
</style>
